// sliceModel.js - 优化版本
import * as THREE from "three";
import { MeshBVH } from "three-mesh-bvh";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils.js";
//import { scene} from '../renderer.js';
import { generateInfillLines } from "./generateInfillLines";


// 引入加载指示器
//import { showLoadingIndicator, hideLoadingIndicator } from './loadingIndicator.js';

// 增加容差值可以减少顶点数量，加快计算速度
const TOLERANCE = 1e-2; // 从1e-3增加到1e-2

// 复用向量和线对象以减少内存分配
const _tempVector = new THREE.Vector3();
const _tempLine = new THREE.Line3();
const _inverseMatrix = new THREE.Matrix4();

// 优化后的三角形处理函数
function processTriangle(tri, localClippingPlane, tempVector, tempLine) {
  const triPoints = [];
  const segments = [];
  let pairs = [
    [tri.a, tri.b],
    [tri.b, tri.c],
    [tri.c, tri.a],
  ];
  
  for (let i = 0; i < 3; i++) {
    const [start, end] = pairs[i];
    tempLine.set(start, end);
    if (localClippingPlane.intersectLine(tempLine, tempVector)) {
      triPoints.push(tempVector.clone());
    }
  }
  
  // 若交点数为奇数，则舍弃最后一个
  if (triPoints.length % 2 !== 0) {
    triPoints.pop();
  }
  
  // 直接构建线段，减少循环
  for (let i = 0; i < triPoints.length; i += 2) {
    segments.push({ p1: triPoints[i], p2: triPoints[i + 1] });
  }
  
  return segments;
}

// 优化连通性分组函数
function groupSegmentsByConnectivity(segments, tolerance = TOLERANCE) {
  // 使用更高效的数据结构
  const uniqueVertices = [];
  const vertexMap = new Map(); // 使用Map而不是线性搜索
  
  function getVertexIndex(v) {
    // 使用近似哈希键来加速查找
    const key = `${Math.round(v.x / tolerance)},${Math.round(v.y / tolerance)},${Math.round(v.z / tolerance)}`;
    if (vertexMap.has(key)) {
      const candidateIndices = vertexMap.get(key);
      for (const idx of candidateIndices) {
        if (v.distanceTo(uniqueVertices[idx]) < tolerance) {
          return idx;
        }
      }
    }
    
    const index = uniqueVertices.length;
    uniqueVertices.push(v.clone());
    
    // 将索引添加到映射
    const key2 = `${Math.round(v.x / tolerance)},${Math.round(v.y / tolerance)},${Math.round(v.z / tolerance)}`;
    if (!vertexMap.has(key2)) {
      vertexMap.set(key2, []);
    }
    vertexMap.get(key2).push(index);
    
    return index;
  }

  const segIndices = [];
  const graph = Array(Math.max(segments.length * 2, 100)).fill().map(() => []);
  
  // 构建图
  for (const seg of segments) {
    const i1 = getVertexIndex(seg.p1);
    const i2 = getVertexIndex(seg.p2);
    segIndices.push({ i1, i2 });
    
    // 检查图大小并动态扩展
    const maxIdx = Math.max(i1, i2);
    while (graph.length <= maxIdx) {
      graph.push([]);
    }
    
    graph[i1].push(i2);
    graph[i2].push(i1);
  }

  // 使用高效的BFS寻找连通分量
  const visited = new Set();
  const components = [];
  
  for (let i = 0; i < uniqueVertices.length; i++) {
    if (!visited.has(i)) {
      const comp = [];
      const queue = [i];
      visited.add(i);
      
      while (queue.length > 0) {
        const v = queue.shift();
        comp.push(v);
        
        for (const nb of graph[v] || []) {
          if (!visited.has(nb)) {
            visited.add(nb);
            queue.push(nb);
          }
        }
      }
      
      components.push(comp);
    }
  }

  // 更高效地分配线段
  const componentSegments = components.map(() => []);
  // 为每个线段创建映射，快速查找它所属的组件
  const vertexComponentMap = new Map();
  
  components.forEach((comp, compIndex) => {
    comp.forEach(vIdx => vertexComponentMap.set(vIdx, compIndex));
  });
  
  for (const seg of segIndices) {
    const compIndex = vertexComponentMap.get(seg.i1);
    // 我们可以安全地假设i1和i2在同一个组件中
    componentSegments[compIndex].push(seg);
  }

  return { uniqueVertices, components, componentSegments };
}

// 优化几何体创建
function createGeometryForComponent(componentSegs, uniqueVertices) {
  // 预分配正确大小的数组
  const numSegments = componentSegs.length;
  const positions = new Float32Array(numSegments * 2 * 3);
  
  // 更高效的循环
  let offset = 0;
  for (let i = 0; i < numSegments; i++) {
    const seg = componentSegs[i];
    const v1 = uniqueVertices[seg.i1];
    const v2 = uniqueVertices[seg.i2];
    
    positions[offset++] = v1.x;
    positions[offset++] = v1.y;
    positions[offset++] = v1.z;
    positions[offset++] = v2.x;
    positions[offset++] = v2.y;
    positions[offset++] = v2.z;
  }
  
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.BufferAttribute(positions, 3));
  return geometry;
}

function tracePolyline(path) {
    const points = [];
    const visited = new Set();
  
    let current = path[0][0];
    points.push([current.x, current.y, current.z]);
    visited.add(current.toArray().join(','));
  
    let found = true;
    while (found) {
      found = false;
      for (let [p1, p2] of path) {
        const p1key = p1.toArray().join(',');
        const p2key = p2.toArray().join(',');
  
        if (p1key === current.toArray().join(',') && !visited.has(p2key)) {
          current = p2;
          points.push([current.x, current.y, current.z]);
          visited.add(p2key);
          found = true;
          break;
        } else if (p2key === current.toArray().join(',') && !visited.has(p1key)) {
          current = p1;
          points.push([current.x, current.y, current.z]);
          visited.add(p1key);
          found = true;
          break;
        }
      }
    }
  
    return points;
  }
  

// 记录当前的切片状态
let isProcessingSlice = false;

// 优化的核心切片函数
function sliceModel(mesh, scene, planeNormal, planeHeight = 0) {
  if (isProcessingSlice) {
    console.log("已有切片操作正在进行中，请等待完成");
    return null;
  }
  
  isProcessingSlice = true;

  
  try {
    if (!mesh || !mesh.geometry) {
      console.error("sliceModel: 需要提供有效的 Mesh");

      isProcessingSlice = false;
      return null;
    }
    
    let geometry = mesh.geometry;
    if (!geometry.index) {
      console.warn("sliceModel: Mesh 需要有索引数据，正在转换...");

      geometry = BufferGeometryUtils.mergeVertices(geometry);
    }
    
    // 使用一个缓存来避免重复创建BVH
    if (!geometry.boundsTree) {

      const bvh = new MeshBVH(geometry, { maxLeafTris: 6 }); // 增加叶子节点上的三角形数量
      geometry.boundsTree = bvh;
    }
    
    const worldClippingPlane = new THREE.Plane(planeNormal, -planeHeight);
    
    _inverseMatrix.copy(mesh.matrixWorld).invert();
    const localClippingPlane = worldClippingPlane.clone().applyMatrix4(_inverseMatrix);
    

    const segments = [];
    
    geometry.boundsTree.shapecast({
      intersectsBounds: (box) => localClippingPlane.intersectsBox(box),
      intersectsTriangle: (tri) => {
        const segs = processTriangle(tri, localClippingPlane, _tempVector, _tempLine);
        segments.push(...segs);
      }
    });
    
    if (segments.length === 0) {
      console.log("没有找到任何交集");

      isProcessingSlice = false;
      return { outlines: [], components: [], componentSegments: [] };
    }
    
    // 如果片段非常多，可以考虑采样
    const maxSegments = 2000; // 设置合理的上限
    if (segments.length > maxSegments) {

      const samplingRate = maxSegments / segments.length;
      const sampledSegments = [];
      for (let i = 0; i < segments.length; i++) {
        if (Math.random() < samplingRate) {
          sampledSegments.push(segments[i]);
        }
      }
      console.log(`片段过多(${segments.length})，采样至${sampledSegments.length}个片段`);
      segments.length = 0;
      segments.push(...sampledSegments);
    }
    

    const connectivity = groupSegmentsByConnectivity(segments);
    const { uniqueVertices, components, componentSegments } = connectivity;
    
    // 过滤掉太小的连通分量
    let minPointsInComponent = 3; // 小于这个值的连通分量将被忽略
    if (components.length > 10) {
      // 如果组件太多，增加忽略阈值
      minPointsInComponent = 5;
    }
    

    const outlines = [];
    const paths = [];
    for (let i = 0; i < componentSegments.length; i++) {
      const compSegs = componentSegments[i];
      if (compSegs.length < minPointsInComponent) continue;
      
      const geom = createGeometryForComponent(compSegs, uniqueVertices);
      // 使用共享材质减少资源消耗
      const material = new THREE.LineBasicMaterial({ color: 0x3288e6 });
      
      const line = new THREE.LineSegments(geom, material);
      line.position.copy(mesh.position);
      line.rotation.copy(mesh.rotation);
      line.scale.copy(mesh.scale);
      line.updateMatrixWorld();
     // scene.add(line);
      outlines.push(line);

       // 构造每个轮廓路径（点对数组）
  const path = [];
  compSegs.forEach(({ i1, i2 }) => {
    path.push([
      uniqueVertices[i1].clone(),
      uniqueVertices[i2].clone()
    ]);
  });
  paths.push(path);

  
    }
    const contourPoints = paths.map(tracePolyline);
    console.log(`切片完成，创建了 ${outlines.length} 个轮廓组件`);
    
    return {
      outlines,
      paths,
      contourPoints, // 👈 加这句
      components,
      componentSegments,
      segments,
      uniqueVertices
    };
  } catch (error) {
    console.error("切片过程中出现错误:", error);
    return { outlines: [], components: [], componentSegments: [] };
  } finally {

    isProcessingSlice = false;
  }
}

/**
 * sliceModelXY：在 z=planeHeight 平面切割
 */
export function sliceModelXY(mesh, scene, planeHeight = 0) {
  return sliceModel(mesh, scene, new THREE.Vector3(0, 0, 1), planeHeight);
}

/**
 * sliceModelXZ：切割平面为 y = planeHeight（平行于 XZ）
 */
export function sliceModelXZ(mesh, scene, planeHeight = 0) {
  return sliceModel(mesh, scene, new THREE.Vector3(0, 1, 0), planeHeight);
}

/**
 * sliceModelYZ：切割平面为 x = planeHeight（平行于 YZ）
 */
export function sliceModelYZ(mesh, scene, planeHeight = 0) {
  return sliceModel(mesh, scene, new THREE.Vector3(1, 0, 0), planeHeight);
}

// 可选：添加一个工作线程版本
export function sliceModelAsync(mesh, scene, planeNormal, planeHeight = 0) {
  return new Promise((resolve) => {
    setTimeout(() => {
      const result = sliceModel(mesh, scene, planeNormal, planeHeight);
      resolve(result);
    }, 0);
  });
}